// Neighborhood Supply Partners — single-page landing.
// IMPORTANT: To replace business contact info, update the BUSINESS object below.
// To replace imagery, swap the URLs in the IMAGES object below.

import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Menu,
    X,
    ArrowRight,
    Shirt,
    Sparkles,
    Cookie,
    BookOpen,
    Briefcase,
    Laptop,
    Truck,
    Users,
    Boxes,
    ClipboardList,
    PackageSearch,
    HandHeart,
    MapPin,
    Mail,
    Phone,
    CheckCircle2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* =====================================================================
   BUSINESS INFO — replace these placeholders with real values when ready.
   ===================================================================== */
const BUSINESS = {
    name: "Neighborhood Supply Partners",
    shortName: "NSP",
    serviceArea: "Metro Detroit, Michigan",
    // TODO: replace with your real contact email
    email: "info@neighborhoodsupplypartners.com",
    // TODO: replace with your real phone number
    phone: "313-242-7228",
    address: "Serving Detroit, Dearborn, Hamtramck, Highland Park & surrounding communities",
};

/* =====================================================================
   IMAGES — curated authentic stock from Pexels/Unsplash.
   Replace any of these with your own photography when available.
   ===================================================================== */
const IMAGES = {
    hero: "/images/hero.png",
    about: "https://images.unsplash.com/photo-1585144374720-64d181405b1c?auto=format&fit=crop&w=1400&q=80",
    support: "/images/how-we-support.png",
    delivery: "/images/delivery.png",
};

const NAV_LINKS = [
    { label: "Home", href: "#home" },
    { label: "About", href: "#about" },
    { label: "Services", href: "#services" },
    { label: "Product Categories", href: "#categories" },
    { label: "Contact", href: "#contact" },
];

/* ---------- Reusable building blocks ---------- */

function Container({ children, className = "" }) {
    return (
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>
            {children}
        </div>
    );
}

function SectionEyebrow({ children }) {
    return (
        <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#0033CC]/70">
            <span className="h-[3px] w-8 bg-[#FFCC00] rounded-full" />
            {children}
        </span>
    );
}

function PrimaryButton({ children, className = "", ...props }) {
    return (
        <button
            className={`bg-[#0033CC] text-white hover:bg-[#1A52E0] transition-colors rounded-md px-6 py-3 font-medium shadow-sm inline-flex items-center justify-center gap-2 ${className}`}
            {...props}
        >
            {children}
        </button>
    );
}

function SecondaryButton({ children, className = "", ...props }) {
    return (
        <button
            className={`bg-transparent border-2 border-[#0033CC] text-[#0033CC] hover:bg-[#F5F3F0] transition-colors rounded-md px-6 py-3 font-medium inline-flex items-center justify-center gap-2 ${className}`}
            {...props}
        >
            {children}
        </button>
    );
}

/* ---------- Header ---------- */

function Header() {
    const [open, setOpen] = useState(false);
    return (
        <header
            data-testid="site-header"
            className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-[#E5E7EB]"
        >
            <Container className="flex items-center justify-between h-16 sm:h-20">
                <a
                    href="#home"
                    data-testid="nav-logo"
                    className="flex items-center gap-2 font-heading font-bold text-[#0033CC]"
                >
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-[#0033CC] text-white">
                        <HandHeart className="h-5 w-5" />
                    </span>
                    <span className="text-base sm:text-lg leading-tight">
                        Neighborhood
                        <span className="block text-xs font-medium text-[#4B5563] tracking-wide">
                            Supply Partners
                        </span>
                    </span>
                </a>

                <nav className="hidden lg:flex items-center gap-8">
                    {NAV_LINKS.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="text-sm font-medium text-[#0033CC]/80 hover:text-[#0033CC] transition-colors"
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                <div className="hidden lg:block">
                    <a href="#contact" data-testid="nav-cta-contact">
                        <PrimaryButton className="px-5 py-2.5 text-sm">
                            Contact Us <ArrowRight className="h-4 w-4" />
                        </PrimaryButton>
                    </a>
                </div>

                <button
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Toggle menu"
                    data-testid="mobile-menu-toggle"
                    className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-md border border-[#E5E7EB] text-[#0033CC]"
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </Container>

            {open && (
                <div className="lg:hidden border-t border-[#E5E7EB] bg-white" data-testid="mobile-menu">
                    <Container className="py-4 flex flex-col gap-3">
                        {NAV_LINKS.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className="py-2 text-base font-medium text-[#0033CC]"
                            >
                                {l.label}
                            </a>
                        ))}
                        <a href="#contact" onClick={() => setOpen(false)}>
                            <PrimaryButton className="w-full mt-2">
                                Contact Us <ArrowRight className="h-4 w-4" />
                            </PrimaryButton>
                        </a>
                    </Container>
                </div>
            )}
        </header>
    );
}

/* ---------- Hero ---------- */

function Hero() {
    return (
        <section id="home" data-testid="hero-section" className="relative bg-white">
            <Container className="py-20 sm:py-28 lg:py-32 grid lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-7 animate-fade-up">
                    <SectionEyebrow>Serving {BUSINESS.serviceArea}</SectionEyebrow>
                    <h1
                        data-testid="hero-headline"
                        className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tight leading-[1.05] text-[#0033CC]"
                    >
                        Supporting Students, Families &amp; Communities Through{" "}
                        <span className="text-[#1A52E0]">Reliable Supply Solutions</span>
                    </h1>
                    <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#4B5563] max-w-2xl">
                        Neighborhood Supply Partners provides dependable access to student
                        essentials — including school uniforms, hygiene products, snacks,
                        educational materials, and local fulfillment support — throughout
                        Metro Detroit.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        <a href="#contact">
                            <PrimaryButton data-testid="hero-cta-contact">
                                Contact Us <ArrowRight className="h-4 w-4" />
                            </PrimaryButton>
                        </a>
                        <a href="#categories">
                            <SecondaryButton data-testid="hero-cta-categories">
                                View Product Categories
                            </SecondaryButton>
                        </a>
                    </div>

                    <dl className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
                        {[
                            { k: "6", v: "Core categories" },
                            { k: "Local", v: "Metro Detroit delivery" },
                            { k: "K–12", v: "School-ready support" },
                        ].map((item) => (
                            <div key={item.v}>
                                <dt className="font-heading text-2xl sm:text-3xl font-bold text-[#0033CC]">
                                    {item.k}
                                </dt>
                                <dd className="mt-1 text-xs sm:text-sm text-[#4B5563]">
                                    {item.v}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>

                <div className="lg:col-span-5 relative">
                    <div className="relative rounded-md overflow-hidden bg-[#F5F3F0] aspect-[4/5] shadow-sm">
                        {/* TODO: Replace hero image in IMAGES.hero */}
                        <img
                            src={IMAGES.hero}
                            alt="Diverse students collaborating in a Metro Detroit school environment"
                            className="absolute inset-0 h-full w-full object-cover"
                            loading="eager"
                        />
                        <div className="absolute inset-0 bg-gradient-to-tr from-[#0033CC]/30 via-transparent to-transparent" />
                    </div>
                    <div className="hidden md:block absolute -bottom-6 -left-6 bg-white border border-[#E5E7EB] rounded-md p-5 shadow-sm max-w-xs">
                        <div className="flex items-center gap-3">
                            <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-[#F5F3F0] text-[#0033CC]">
                                <Truck className="h-5 w-5" />
                            </span>
                            <div>
                                <p className="font-heading font-semibold text-sm text-[#0033CC]">
                                    Local delivery
                                </p>
                                <p className="text-xs text-[#4B5563]">
                                    Across Metro Detroit
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </Container>
        </section>
    );
}

/* ---------- About / Mission ---------- */

function About() {
    return (
        <section id="about" data-testid="about-section" className="bg-[#F5F3F0]">
            <Container className="py-24 sm:py-32 grid lg:grid-cols-12 gap-12 items-start">
                <div className="lg:col-span-5">
                    <SectionEyebrow>About &amp; Mission</SectionEyebrow>
                    <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0033CC] tracking-tight">
                        Built to support the communities we serve.
                    </h2>
                    <div className="mt-6 rounded-md overflow-hidden aspect-[5/4] bg-[#EAE6E1]">
                        {/* TODO: Replace about image in IMAGES.about */}
                        <img
                            src={IMAGES.about}
                            alt="A supportive community learning environment in Metro Detroit"
                            className="h-full w-full object-cover"
                            loading="lazy"
                        />
                    </div>
                </div>

                <div className="lg:col-span-7 lg:pt-16 space-y-6 text-[#4B5563] text-base sm:text-lg leading-relaxed">
                    <p>
                        Neighborhood Supply Partners was founded with a commitment to
                        supporting students, families, and communities throughout Metro
                        Detroit by providing access to essential products that help
                        students and families feel prepared, confident, and supported
                        every day.
                    </p>
                    <p>
                        Our mission is to deliver dependable access to student and family
                        essentials — including school uniforms, personal hygiene products,
                        snacks, educational materials, and other everyday support items —
                        through responsive service, reliable fulfillment, and local
                        delivery solutions.
                    </p>
                    <p>
                        As a locally focused organization, we are committed to building
                        strong community relationships and creating practical support
                        systems that positively impact students, families, educators, and
                        neighborhoods throughout the communities we serve.
                    </p>

                    <ul className="grid sm:grid-cols-2 gap-3 pt-4">
                        {[
                            "Community-centered service",
                            "Reliable, on-time fulfillment",
                            "Practical, everyday essentials",
                            "Locally rooted in Metro Detroit",
                        ].map((item) => (
                            <li key={item} className="flex items-start gap-3">
                                <CheckCircle2 className="h-5 w-5 text-[#0033CC] flex-shrink-0 mt-0.5" />
                                <span className="text-[#0033CC] font-medium text-sm sm:text-base">
                                    {item}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </Container>
        </section>
    );
}

/* ---------- Services ---------- */

const SERVICES = [
    {
        icon: Shirt,
        title: "School Uniform Distribution",
        body: "Coordinated sizing, ordering, and distribution of school uniforms for K–12 students, school staff, and families.",
    },
    {
        icon: Sparkles,
        title: "Hygiene & Personal Care Products",
        body: "Reliable supply of essential hygiene items that help students feel prepared and confident throughout the school day.",
    },
    {
        icon: Cookie,
        title: "Snacks & Student Essentials",
        body: "Wholesome snacks and student-ready essentials that support classroom programs, after-school care, and community events.",
    },
    {
        icon: BookOpen,
        title: "Educational Materials",
        body: "Classroom-ready learning supplies, supplemental materials, and student kits sourced to support educators and families.",
    },
    {
        icon: Truck,
        title: "Local Delivery Support",
        body: "Direct delivery across Metro Detroit to schools, organizations, and community partners — on the schedule you need.",
    },
    {
        icon: Boxes,
        title: "Community Fulfillment Services",
        body: "Custom fulfillment for community programs, family drives, and district-wide initiatives — assembled and delivered locally.",
    },
];

function Services() {
    return (
        <section id="services" data-testid="services-section" className="bg-white">
            <Container className="py-24 sm:py-32">
                <div className="max-w-3xl">
                    <SectionEyebrow>What we do</SectionEyebrow>
                    <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0033CC] tracking-tight">
                        Practical services for schools, families, and community partners.
                    </h2>
                    <p className="mt-5 text-base sm:text-lg text-[#4B5563] leading-relaxed">
                        Six core services designed to take the weight of sourcing and
                        fulfillment off your team — so educators, administrators, and
                        community leaders can stay focused on the people they serve.
                    </p>
                </div>

                <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                    {SERVICES.map(({ icon: Icon, title, body }) => (
                        <article
                            key={title}
                            data-testid={`service-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className="group bg-white border border-[#E5E7EB] rounded-md p-8 hover:-translate-y-1 hover:border-[#0033CC]/30 transition-all duration-300 shadow-sm"
                        >
                            <span className="inline-flex h-12 w-12 items-center justify-center rounded-md bg-[#F5F3F0] text-[#0033CC] group-hover:bg-[#0033CC] group-hover:text-white transition-colors">
                                <Icon className="h-6 w-6" />
                            </span>
                            <h3 className="mt-6 font-heading text-xl font-semibold text-[#0033CC]">
                                {title}
                            </h3>
                            <p className="mt-3 text-[#4B5563] leading-relaxed text-sm sm:text-base">
                                {body}
                            </p>
                        </article>
                    ))}
                </div>
            </Container>
        </section>
    );
}

/* ---------- Product Categories ---------- */

const CATEGORIES = [
    { icon: Shirt, title: "School Uniforms", body: "Polos, bottoms, outerwear and accessories for K–12 dress codes." },
    { icon: Sparkles, title: "Hygiene Products", body: "Personal care essentials for students, families, and classrooms." },
    { icon: Cookie, title: "Snacks & Student Essentials", body: "Classroom-friendly snacks and student-ready essentials." },
    { icon: BookOpen, title: "Educational Materials", body: "Workbooks, supplemental learning aids, and classroom kits." },
    { icon: Briefcase, title: "Office & Administrative Supplies", body: "Everyday admin supplies for schools and community offices." },
    { icon: Laptop, title: "Technology Accessories", body: "Cables, cases, peripherals and classroom-ready tech accessories." },
];

function Categories() {
    return (
        <section id="categories" data-testid="categories-section" className="bg-[#FFF6CC]/60">
            <Container className="py-24 sm:py-32">
                <div className="max-w-3xl">
                    <SectionEyebrow>Product categories</SectionEyebrow>
                    <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0033CC] tracking-tight">
                        Essentials we source and supply locally.
                    </h2>
                    <p className="mt-5 text-base sm:text-lg text-[#4B5563] leading-relaxed">
                        We focus on the categories that schools, families, and community
                        partners depend on most. Reach out to discuss specific products,
                        quantities, or program needs.
                    </p>
                </div>

                <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                    {CATEGORIES.map(({ icon: Icon, title, body }) => (
                        <article
                            key={title}
                            data-testid={`category-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className="bg-white border border-[#E5E7EB] rounded-md p-8 hover:-translate-y-1 transition-all duration-300 shadow-sm flex flex-col"
                        >
                            <div className="flex items-start justify-between">
                                <span className="inline-flex h-12 w-12 items-center justify-center rounded-md bg-[#0033CC] text-white">
                                    <Icon className="h-6 w-6" />
                                </span>
                                <span className="text-xs font-medium uppercase tracking-wider text-[#9CA3AF]">
                                    Category
                                </span>
                            </div>
                            <h3 className="mt-6 font-heading text-xl font-semibold text-[#0033CC]">
                                {title}
                            </h3>
                            <p className="mt-3 text-[#4B5563] leading-relaxed text-sm sm:text-base flex-1">
                                {body}
                            </p>
                            <a
                                href="#contact"
                                className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#0033CC] hover:text-[#1A52E0]"
                            >
                                Inquire about this category{" "}
                                <ArrowRight className="h-4 w-4" />
                            </a>
                        </article>
                    ))}
                </div>
            </Container>
        </section>
    );
}

/* ---------- How We Support (3 steps) ---------- */

const STEPS = [
    {
        icon: ClipboardList,
        step: "01",
        title: "Identify Needs",
        body: "We meet with schools, organizations, and community partners to understand the specific essentials students and families need.",
    },
    {
        icon: PackageSearch,
        step: "02",
        title: "Source & Fulfill",
        body: "We source, organize, and prepare the right mix of uniforms, hygiene products, snacks, and supplies for each program.",
    },
    {
        icon: Truck,
        step: "03",
        title: "Deliver Locally",
        body: "We deliver directly across Metro Detroit on a schedule that works for your team, classroom, or community event.",
    },
];

function HowWeSupport() {
    return (
        <section id="how" data-testid="how-section" className="bg-white">
            <Container className="py-24 sm:py-32 grid lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-5">
                    <SectionEyebrow>How we support</SectionEyebrow>
                    <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0033CC] tracking-tight">
                        A straightforward process, built for reliability.
                    </h2>
                    <p className="mt-5 text-base sm:text-lg text-[#4B5563] leading-relaxed">
                        We keep the process clear and operationally simple — so school
                        leaders, educators, and community partners always know what to
                        expect.
                    </p>
                    <div className="mt-8 rounded-md overflow-hidden aspect-[5/4] bg-[#F5F3F0]">
                        {/* TODO: Replace support image in IMAGES.support */}
                        <img
                            src={IMAGES.support}
                            alt="Students learning together in a Metro Detroit community setting"
                            className="h-full w-full object-cover"
                            loading="lazy"
                        />
                    </div>
                </div>

                <ol className="lg:col-span-7 space-y-6">
                    {STEPS.map(({ icon: Icon, step, title, body }) => (
                        <li
                            key={step}
                            data-testid={`step-${step}`}
                            className="relative bg-white border border-[#E5E7EB] rounded-md p-8 hover:border-[#0033CC]/30 transition-colors"
                        >
                            <div className="flex items-start gap-6">
                                <span className="inline-flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-md bg-[#FFCC00] text-[#0033CC] font-heading font-bold text-lg shadow-sm">
                                    {step}
                                </span>
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 text-[#0033CC]/70">
                                        <Icon className="h-5 w-5" />
                                        <span className="text-xs font-semibold uppercase tracking-[0.16em]">
                                            Step
                                        </span>
                                    </div>
                                    <h3 className="mt-1 font-heading text-2xl font-semibold text-[#0033CC]">
                                        {title}
                                    </h3>
                                    <p className="mt-2 text-[#4B5563] leading-relaxed text-sm sm:text-base">
                                        {body}
                                    </p>
                                </div>
                            </div>
                        </li>
                    ))}
                </ol>
            </Container>
        </section>
    );
}

/* ---------- Local Delivery feature band ---------- */

function DeliveryBand() {
    return (
        <section id="delivery" data-testid="delivery-section" className="bg-[#0033CC] text-white">
            <Container className="py-20 sm:py-28 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                <div className="lg:col-span-6 order-2 lg:order-1">
                    <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#FFCC00]">
                        <span className="h-[3px] w-8 bg-[#FFCC00] rounded-full" />
                        Local delivery
                    </span>
                    <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
                        Hand-delivered across Metro Detroit.
                    </h2>
                    <p className="mt-5 text-base sm:text-lg leading-relaxed text-white/85 max-w-xl">
                        We coordinate every drop-off with your team so supplies arrive ready to use —
                        sorted, sealed, and on the schedule that works for your classrooms,
                        programs, and community events.
                    </p>
                    <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                        {[
                            "Direct drop-off to your site",
                            "Flexible delivery windows",
                            "Sorted, sealed, ready to use",
                            "Local team, local trucks",
                        ].map((item) => (
                            <li key={item} className="flex items-start gap-3 text-sm sm:text-base">
                                <CheckCircle2 className="h-5 w-5 text-[#FFCC00] flex-shrink-0 mt-0.5" />
                                <span className="text-white/90">{item}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-9">
                        <a href="#contact">
                            <button
                                data-testid="delivery-cta-contact"
                                className="bg-[#FFCC00] text-[#0033CC] hover:bg-white transition-colors rounded-md px-6 py-3 font-semibold shadow-sm inline-flex items-center gap-2"
                            >
                                Schedule a delivery <ArrowRight className="h-4 w-4" />
                            </button>
                        </a>
                    </div>
                </div>

                <div className="lg:col-span-6 order-1 lg:order-2">
                    <div className="relative rounded-md overflow-hidden bg-[#00228A] aspect-[3/2] shadow-lg ring-1 ring-white/10">
                        {/* TODO: Replace delivery image in IMAGES.delivery */}
                        <img
                            src={IMAGES.delivery}
                            alt="A delivery professional handing school supplies to a Metro Detroit school staff member"
                            className="absolute inset-0 h-full w-full object-cover"
                            loading="lazy"
                        />
                    </div>
                </div>
            </Container>
        </section>
    );
}

/* ---------- Contact ---------- */

function Contact() {
    const [form, setForm] = useState({
        name: "",
        organization: "",
        email: "",
        phone: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const updateField = (key) => (e) =>
        setForm((f) => ({ ...f, [key]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Please fill in name, email, and message.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/contact`, {
                name: form.name.trim(),
                organization: form.organization.trim() || null,
                email: form.email.trim(),
                phone: form.phone.trim() || null,
                message: form.message.trim(),
            });
            toast.success("Thank you — we'll be in touch shortly.");
            setForm({ name: "", organization: "", email: "", phone: "", message: "" });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(
                typeof detail === "string"
                    ? detail
                    : "Something went wrong. Please try again or email us directly."
            );
        } finally {
            setSubmitting(false);
        }
    };

    const inputClass =
        "border border-[#E5E7EB] rounded-md px-4 py-3 bg-white focus:border-[#0033CC] focus:ring-1 focus:ring-[#0033CC] focus:outline-none w-full transition-colors text-[#0033CC] placeholder:text-[#9CA3AF]";
    const labelClass = "block text-sm font-medium text-[#0033CC] mb-1.5";

    return (
        <section id="contact" data-testid="contact-section" className="bg-[#F5F3F0]">
            <Container className="py-24 sm:py-32 grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <SectionEyebrow>Get in touch</SectionEyebrow>
                    <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0033CC] tracking-tight">
                        Let&apos;s build a reliable supply plan for your community.
                    </h2>
                    <p className="mt-5 text-base sm:text-lg text-[#4B5563] leading-relaxed">
                        Whether you&apos;re a school district, community organization, or
                        family program — share a few details and we&apos;ll follow up
                        promptly to discuss how we can help.
                    </p>

                    <ul className="mt-10 space-y-5">
                        <li className="flex items-start gap-4">
                            <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-white border border-[#E5E7EB] text-[#0033CC]">
                                <MapPin className="h-5 w-5" />
                            </span>
                            <div>
                                <p className="font-semibold text-[#0033CC]">Service area</p>
                                <p className="text-sm text-[#4B5563]">{BUSINESS.address}</p>
                            </div>
                        </li>
                        <li className="flex items-start gap-4">
                            <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-white border border-[#E5E7EB] text-[#0033CC]">
                                <Mail className="h-5 w-5" />
                            </span>
                            <div>
                                <p className="font-semibold text-[#0033CC]">Email</p>
                                <a
                                    href={`mailto:${BUSINESS.email}`}
                                    data-testid="contact-info-email"
                                    className="text-sm text-[#4B5563] hover:text-[#0033CC]"
                                >
                                    {BUSINESS.email}
                                </a>
                            </div>
                        </li>
                        <li className="flex items-start gap-4">
                            <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-white border border-[#E5E7EB] text-[#0033CC]">
                                <Phone className="h-5 w-5" />
                            </span>
                            <div>
                                <p className="font-semibold text-[#0033CC]">Phone</p>
                                <a
                                    href={`tel:${BUSINESS.phone.replace(/[^0-9+]/g, "")}`}
                                    data-testid="contact-info-phone"
                                    className="text-sm text-[#4B5563] hover:text-[#0033CC]"
                                >
                                    {BUSINESS.phone}
                                </a>
                            </div>
                        </li>
                    </ul>
                </div>

                <form
                    onSubmit={handleSubmit}
                    data-testid="contact-form"
                    className="lg:col-span-7 bg-white border border-[#E5E7EB] rounded-md p-6 sm:p-10 shadow-sm"
                    noValidate
                >
                    <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                            <label htmlFor="name" className={labelClass}>
                                Name <span className="text-[#1A52E0]">*</span>
                            </label>
                            <input
                                id="name"
                                type="text"
                                required
                                value={form.name}
                                onChange={updateField("name")}
                                placeholder="Your full name"
                                className={inputClass}
                                data-testid="contact-input-name"
                            />
                        </div>
                        <div>
                            <label htmlFor="organization" className={labelClass}>
                                Organization
                            </label>
                            <input
                                id="organization"
                                type="text"
                                value={form.organization}
                                onChange={updateField("organization")}
                                placeholder="School, district, nonprofit, etc."
                                className={inputClass}
                                data-testid="contact-input-organization"
                            />
                        </div>
                        <div>
                            <label htmlFor="email" className={labelClass}>
                                Email <span className="text-[#1A52E0]">*</span>
                            </label>
                            <input
                                id="email"
                                type="email"
                                required
                                value={form.email}
                                onChange={updateField("email")}
                                placeholder="you@organization.org"
                                className={inputClass}
                                data-testid="contact-input-email"
                            />
                        </div>
                        <div>
                            <label htmlFor="phone" className={labelClass}>
                                Phone
                            </label>
                            <input
                                id="phone"
                                type="tel"
                                value={form.phone}
                                onChange={updateField("phone")}
                                placeholder="(313) 555-0123"
                                className={inputClass}
                                data-testid="contact-input-phone"
                            />
                        </div>
                    </div>

                    <div className="mt-5">
                        <label htmlFor="message" className={labelClass}>
                            Message <span className="text-[#1A52E0]">*</span>
                        </label>
                        <textarea
                            id="message"
                            required
                            rows={6}
                            value={form.message}
                            onChange={updateField("message")}
                            placeholder="Tell us a bit about your program, students served, and the categories you're interested in."
                            className={`${inputClass} resize-y min-h-[140px]`}
                            data-testid="contact-input-message"
                        />
                    </div>

                    <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <p className="text-xs text-[#6B7280]">
                            By submitting, you agree we may contact you about your inquiry.
                        </p>
                        <PrimaryButton
                            type="submit"
                            disabled={submitting}
                            data-testid="contact-submit-button"
                            className="disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {submitting ? "Sending…" : "Submit Request"}
                            {!submitting && <ArrowRight className="h-4 w-4" />}
                        </PrimaryButton>
                    </div>
                </form>
            </Container>
        </section>
    );
}

/* ---------- Footer ---------- */

function Footer() {
    return (
        <footer
            data-testid="site-footer"
            className="bg-[#0033CC] text-white"
        >
            <Container className="py-16 grid md:grid-cols-3 gap-10">
                <div>
                    <div className="flex items-center gap-2 font-heading font-bold">
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-white text-[#0033CC]">
                            <HandHeart className="h-5 w-5" />
                        </span>
                        <span>
                            Neighborhood Supply Partners
                        </span>
                    </div>
                    <p className="mt-4 text-sm text-white/70 leading-relaxed max-w-xs">
                        A community supply and fulfillment partner supporting students,
                        families, and schools across Metro Detroit.
                    </p>
                </div>
                <div>
                    <h4 className="font-heading font-semibold text-sm uppercase tracking-[0.16em] text-white/60">
                        Service Area
                    </h4>
                    <p className="mt-4 text-sm text-white/80 leading-relaxed">
                        {BUSINESS.serviceArea}
                    </p>
                    <p className="mt-2 text-sm text-white/60 leading-relaxed">
                        {BUSINESS.address}
                    </p>
                </div>
                <div>
                    <h4 className="font-heading font-semibold text-sm uppercase tracking-[0.16em] text-white/60">
                        Contact
                    </h4>
                    <ul className="mt-4 space-y-2 text-sm">
                        <li>
                            <a
                                href={`mailto:${BUSINESS.email}`}
                                className="text-white/80 hover:text-white"
                                data-testid="footer-email"
                            >
                                {BUSINESS.email}
                            </a>
                        </li>
                        <li>
                            <a
                                href={`tel:${BUSINESS.phone.replace(/[^0-9+]/g, "")}`}
                                className="text-white/80 hover:text-white"
                                data-testid="footer-phone"
                            >
                                {BUSINESS.phone}
                            </a>
                        </li>
                    </ul>
                </div>
            </Container>
            <div className="border-t border-white/10">
                <Container className="py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/60">
                    <p>
                        © {new Date().getFullYear()} {BUSINESS.name}. All rights reserved.
                    </p>
                    <p>
                        Proudly serving {BUSINESS.serviceArea}.
                    </p>
                </Container>
            </div>
        </footer>
    );
}

/* ---------- Landing ---------- */

export default function Landing() {
    return (
        <div className="App font-body text-[#0033CC]">
            <Header />
            <main>
                <Hero />
                <About />
                <Services />
                <Categories />
                <HowWeSupport />
                <DeliveryBand />
                <Contact />
            </main>
            <Footer />
        </div>
    );
}

